import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Equipment } from 'src/app/bo/equipment';
import { Operator } from 'src/app/bo/operator';
import { DrillActivityService } from 'src/app/services/drill-activity.service';
import { DrillSessionApiDto } from 'src/app/shared/models/drilling.models';

export interface SessionFormDialogData {
  blastId: number;
  siteId: number;
  session?: DrillSessionApiDto;
}

function toDatetimeLocal(isoStr: string | undefined): string {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function currentDatetimeLocal(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

@Component({
  selector: 'blast-drilling-session-form-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, FormsModule],
  template: `
    <h2 mat-dialog-title>{{ isEdit ? 'Edit Drilling Session' : 'Add Drilling Session' }}</h2>

    <mat-dialog-content class="session-form-content">
      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Session Start</mat-label>
          <input matInput type="datetime-local" [(ngModel)]="formData.sessionStart" name="sessionStart" required>
        </mat-form-field>
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="sessionEndInvalid">
            <mat-label>Session End </mat-label>
            <input matInput type="datetime-local" [(ngModel)]="formData.sessionEnd" name="sessionEnd" required>
          </mat-form-field>
          @if (sessionEndInvalid) {
            <div class="field-error-msg">End must be after session start</div>
          }
        </div>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Drill Rig</mat-label>
          <mat-select [(ngModel)]="formData.drillRigId" name="drillRigId" required>
            @for (eq of equipments(); track eq.equipementId) {
              <mat-option [value]="eq.equipementId">{{ eq.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Operator</mat-label>
          <mat-select [(ngModel)]="formData.operatorId" name="operatorId" required>
            @for (op of operators(); track op.operatorId) {
              <mat-option [value]="op.operatorId">{{ op.nom }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Engine Hours Start</mat-label>
          <input matInput type="number" [(ngModel)]="formData.engineHoursStart" name="engineHoursStart" min="0">
        </mat-form-field>
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="engineHoursEndInvalid">
            <mat-label>Engine Hours End</mat-label>
            <input matInput type="number" [(ngModel)]="formData.engineHoursEnd" name="engineHoursEnd" min="0">
          </mat-form-field>
          @if (engineHoursEndInvalid) {
            <div class="field-error-msg">End must be ≥ engine hours start</div>
          }
        </div>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Drifter Hours Start</mat-label>
          <input matInput type="number" [(ngModel)]="formData.drifterHoursStart" name="drifterHoursStart" min="0">
        </mat-form-field>
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="drifterHoursEndInvalid">
            <mat-label>Drifter Hours End</mat-label>
            <input matInput type="number" [(ngModel)]="formData.drifterHoursEnd" name="drifterHoursEnd" min="0">
          </mat-form-field>
          @if (drifterHoursEndInvalid) {
            <div class="field-error-msg">End must be ≥ drifter hours start</div>
          }
        </div>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Total Pause Hours</mat-label>
          <input matInput type="number" [(ngModel)]="formData.totalPauseHours" name="totalPauseHours" min="0">
        </mat-form-field>
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Total Drilled Length (m)</mat-label>
          <input matInput type="number" [(ngModel)]="formData.totalDrilledLength" name="totalDrilledLength" min="0">
        </mat-form-field>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-button color="primary" [disabled]="!isValid" (click)="onSubmit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .session-form-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 520px;
      padding-top: 8px;
    }
    .form-row {
      display: flex;
      gap: 16px;
    }
    .form-field-wrap {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .form-field {
      flex: 1;
      width: 100%;
    }
    .field-error-msg {
      color: #f44336;
      font-size: 12px;
      margin-top: -16px;
      padding-left: 16px;
      line-height: 1.4;
    }
    .field-error-active ::ng-deep .mdc-notched-outline__leading,
    .field-error-active ::ng-deep .mdc-notched-outline__notch,
    .field-error-active ::ng-deep .mdc-notched-outline__trailing {
      border-color: #f44336 !important;
    }
    .field-error-active ::ng-deep .mdc-floating-label {
      color: #f44336 !important;
    }
  `],
})
export class BlastDrillingSessionFormDialogComponent {
  private readonly drillActivityService = inject(DrillActivityService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isEdit: boolean;
  protected readonly equipments = signal<Equipment[]>([]);
  protected readonly operators = signal<Operator[]>([]);

  protected formData: {
    sessionStart: string;
    sessionEnd: string;
    drillRigId: number | null;
    operatorId: number | null;
    engineHoursStart: number | null;
    engineHoursEnd: number | null;
    drifterHoursStart: number | null;
    drifterHoursEnd: number | null;
    totalPauseHours: number | null;
    totalDrilledLength: number;
  };

  constructor(
    private readonly dialogRef: MatDialogRef<BlastDrillingSessionFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: SessionFormDialogData,
  ) {
    this.isEdit = !!data.session;
    const s = data.session;
    this.formData = {
      sessionStart: toDatetimeLocal(s?.sessionStartUtc) || currentDatetimeLocal(),
      sessionEnd: toDatetimeLocal(s?.sessionEndUtc),
      drillRigId: null,
      operatorId: null,
      engineHoursStart: s?.engineHoursStart ?? null,
      engineHoursEnd: s?.engineHoursEnd ?? null,
      drifterHoursStart: s?.drifterHoursStart ?? null,
      drifterHoursEnd: s?.drifterHoursEnd ?? null,
      totalPauseHours: s?.totalPauseHours ?? null,
      totalDrilledLength: s?.totalDrilledLength ?? 0,
    };

    forkJoin([
      this.drillActivityService.fetchEquipmentBySite(data.siteId),
      this.drillActivityService.fetchOperatorsBySite(data.siteId),
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([equipList, opList]) => {
        this.equipments.set(equipList);
        this.operators.set(opList);
        // Preselect only after options exist so mat-select can match the value
        this.formData.drillRigId = s?.drillRigId ?? null;
        this.formData.operatorId = s?.operatorId ?? null;
      });
  }

  protected get sessionEndInvalid(): boolean {
    return !!this.formData.sessionEnd && !!this.formData.sessionStart &&
      new Date(this.formData.sessionEnd) <= new Date(this.formData.sessionStart);
  }

  protected get engineHoursEndInvalid(): boolean {
    return this.formData.engineHoursEnd !== null && this.formData.engineHoursStart !== null &&
      this.formData.engineHoursEnd < this.formData.engineHoursStart;
  }

  protected get drifterHoursEndInvalid(): boolean {
    return this.formData.drifterHoursEnd !== null && this.formData.drifterHoursStart !== null &&
      this.formData.drifterHoursEnd < this.formData.drifterHoursStart;
  }

  protected get isValid(): boolean {
    return !!this.formData.sessionStart &&
      !!this.formData.sessionEnd &&
      !!this.formData.drillRigId &&
      !!this.formData.operatorId &&
      !this.sessionEndInvalid &&
      !this.engineHoursEndInvalid &&
      !this.drifterHoursEndInvalid;
  }

  protected onSubmit(): void {
    const dto: DrillSessionApiDto = {
      id: this.data.session?.id ?? '',
      blastId: this.data.blastId,
      drillRigId: this.formData.drillRigId ?? undefined,
      drillRigName: this.equipments().find(e => e.equipementId === this.formData.drillRigId)?.name || undefined,
      operatorId: this.formData.operatorId ?? undefined,
      operatorName: this.operators().find(o => o.operatorId === this.formData.operatorId)?.nom || undefined,
      sessionStartUtc: new Date(this.formData.sessionStart).toISOString(),
      sessionEndUtc: this.formData.sessionEnd
        ? new Date(this.formData.sessionEnd).toISOString()
        : undefined,
      engineHoursStart: this.formData.engineHoursStart ?? undefined,
      engineHoursEnd: this.formData.engineHoursEnd ?? undefined,
      drifterHoursStart: this.formData.drifterHoursStart ?? undefined,
      drifterHoursEnd: this.formData.drifterHoursEnd ?? undefined,
      totalPauseHours: this.formData.totalPauseHours ?? undefined,
      totalDrilledLength: this.formData.totalDrilledLength ?? 0,
      numberOfHolesDrilled: this.data.session?.numberOfHolesDrilled ?? 0,
    };
    this.dialogRef.close(dto);
  }
}
